import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';

const THX_RED = '#B71530';

function apiError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join('; ');
  return String(detail);
}

const ALL_TABS = [
  { id: 'imports',    label: 'Data Imports',         featureKey: 'data_imports',        adminOnly: false },
  { id: 'azure',      label: 'Azure Configuration',  featureKey: 'azure_config',        adminOnly: false },
  { id: 'tolerances', label: 'Matching Tolerances',  featureKey: 'matching_tolerances', adminOnly: false },
  { id: 'users',      label: 'User Management',      featureKey: null,                  adminOnly: true  },
  { id: 'branding',   label: 'Branding',             featureKey: 'branding',            adminOnly: false, appAdminOnly: true },
  { id: 'export',     label: 'Global Settings',       featureKey: 'global_settings',     adminOnly: true,  appAdminOnly: true },
];

export default function Settings() {
  const { branding } = useOrg();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAppAdmin = user?.role === 'app_admin';
  const [features, setFeatures] = useState({});
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('imports');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteStep2, setDeleteStep2] = useState(false);
  const [deleteWord, setDeleteWord] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState('');
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: 'info' });

  const [azureForm, setAzureForm] = useState({
    tenant_id: '', client_id: '', client_secret: '',
    doc_intelligence_endpoint: '', doc_intelligence_key: '',
  });
  const [tolForm, setTolForm] = useState({
    amount_tolerance: 0.5, tax_rounding_tolerance: 0.02,
    date_tolerance_days: 3, fuzzy_match_threshold: 80, confidence_threshold: 0.85,
  });
  const [mailboxes, setMailboxes] = useState('');
  const [newUser, setNewUser] = useState({ email: '', display_name: '', role: 'reviewer', password: '' });
  const [sageFile, setSageFile] = useState(null);
  const [syrinxFile, setSyrinxFile] = useState(null);
  const [importing, setImporting] = useState({ sage: false, syrinx: false, matching: false });
  const [brandingForm, setBrandingForm] = useState({ display_name: '', primary_color: '#B71530' });
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploadingBranding, setUploadingBranding] = useState({ logo: false, favicon: false });

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'info' }), 4000);
  };

  const fetchAll = useCallback(() => {
    setLoading(true);
    const requests = [client.get('/settings/config')];
    if (isAdmin) requests.push(client.get('/settings/users'));
    Promise.all(requests)
      .then(([cfg, usr]) => {
        const c = cfg.data;
        setConfig(c);
        setAzureForm({
          tenant_id: c.azure?.tenant_id || '',
          client_id: c.azure?.client_id || '',
          client_secret: '',
          doc_intelligence_endpoint: c.azure?.doc_intelligence_endpoint || '',
          doc_intelligence_key: '',
        });
        setTolForm({
          amount_tolerance: c.tolerances?.amount_tolerance ?? 0.5,
          tax_rounding_tolerance: c.tolerances?.tax_rounding_tolerance ?? 0.02,
          date_tolerance_days: c.tolerances?.date_tolerance_days ?? 3,
          fuzzy_match_threshold: c.tolerances?.fuzzy_match_threshold ?? 80,
          confidence_threshold: c.tolerances?.confidence_threshold ?? 0.85,
        });
        setMailboxes((c.mailboxes || []).join('\n'));
        if (usr) setUsers(usr.data.users || []);
        setBrandingForm({
          display_name: branding.display_name || '',
          primary_color: branding.primary_color || '#B71530',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    client.get('/orgs/my-features')
      .then(r => {
        const f = r.data.features || {};
        setFeatures(f);
        const visibleTabs = ALL_TABS.filter(t =>
          (!t.featureKey || f[t.featureKey] !== false) && (!t.adminOnly || isAdmin) && (!t.appAdminOnly || isAppAdmin)
        );
        if (visibleTabs.length > 0 && !visibleTabs.find(t => t.id === activeTab)) {
          setActiveTab(visibleTabs[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setFeaturesLoaded(true));
  }, []);

  const handleSaveAzure = async (e) => {
    e.preventDefault();
    try {
      await client.patch('/settings/config', { azure: azureForm });
      showMsg('Azure configuration saved');
    } catch (err) {
      showMsg(apiError(err, 'Save failed'), 'danger');
    }
  };

  const handleSaveTolerance = async (e) => {
    e.preventDefault();
    try {
      await client.patch('/settings/config', {
        tolerances: {
          ...tolForm,
          amount_tolerance: parseFloat(tolForm.amount_tolerance),
          tax_rounding_tolerance: parseFloat(tolForm.tax_rounding_tolerance),
          date_tolerance_days: parseInt(tolForm.date_tolerance_days),
          fuzzy_match_threshold: parseInt(tolForm.fuzzy_match_threshold),
          confidence_threshold: parseFloat(tolForm.confidence_threshold),
        },
      });
      showMsg('Tolerances saved');
    } catch (err) {
      showMsg(apiError(err, 'Save failed'), 'danger');
    }
  };

  const handleSaveMailboxes = async (e) => {
    e.preventDefault();
    const list = mailboxes.split('\n').map(m => m.trim()).filter(Boolean);
    try {
      await client.patch('/settings/config', { mailboxes: { mailboxes: list } });
      showMsg('Mailboxes saved');
    } catch (err) {
      showMsg(apiError(err, 'Save failed'), 'danger');
    }
  };

  const handleSageImport = async () => {
    if (!sageFile) return showMsg('Please select a file', 'warning');
    setImporting(p => ({ ...p, sage: true }));
    const form = new FormData();
    form.append('file', sageFile);
    try {
      const r = await client.post('/settings/sage-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMsg(`Sage import complete: ${r.data.stats?.imported || 0} rows imported`);
      setSageFile(null);
      fetchAll();
    } catch (err) {
      showMsg(apiError(err, 'Import failed'), 'danger');
    } finally {
      setImporting(p => ({ ...p, sage: false }));
    }
  };

  const handleSyrinxImport = async () => {
    if (!syrinxFile) return showMsg('Please select a file', 'warning');
    setImporting(p => ({ ...p, syrinx: true }));
    const form = new FormData();
    form.append('file', syrinxFile);
    try {
      const r = await client.post('/settings/syrinx-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMsg(`Syrinx import complete: ${r.data.stats?.imported || 0} rows imported`);
      setSyrinxFile(null);
      fetchAll();
    } catch (err) {
      showMsg(apiError(err, 'Import failed'), 'danger');
    } finally {
      setImporting(p => ({ ...p, syrinx: false }));
    }
  };

  const handleRunMatching = async () => {
    setImporting(p => ({ ...p, matching: true }));
    try {
      const r = await client.post('/settings/run-matching');
      showMsg(`Matching complete: ${r.data.invoice_matching?.matched || 0} matched`);
    } catch (err) {
      showMsg(apiError(err, 'Matching failed'), 'danger');
    } finally {
      setImporting(p => ({ ...p, matching: false }));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await client.post('/settings/users', newUser);
      showMsg('User created');
      setNewUser({ email: '', display_name: '', role: 'reviewer', password: '' });
      fetchAll();
    } catch (err) {
      showMsg(apiError(err, 'Create failed'), 'danger');
    }
  };

  const handleUserRoleChange = async (user, role) => {
    try {
      await client.patch(`/settings/users/${user.id}`, { role });
      showMsg('User updated');
      fetchAll();
    } catch (err) {
      showMsg(apiError(err, 'Update failed'), 'danger');
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const r = await client.get(`/settings/export?format=${format}`, { responseType: 'blob' });
      const orgName = (branding.display_name || 'org').replace(/\s+/g, '_');
      const filename = format === 'csv' ? `${orgName}_invoices.csv` : `${orgName}_export.json`;
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showMsg('Export failed', 'danger');
    } finally {
      setExporting('');
    }
  };

  const handleDeleteOrg = async () => {
    setDeleting(true);
    try {
      await client.delete('/settings/org', { data: { confirmation: deleteConfirm } });
      await logout();
    } catch (err) {
      showMsg(apiError(err, 'Deletion failed'), 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.email}?`)) return;
    try {
      await client.delete(`/settings/users/${user.id}`);
      showMsg('User deactivated');
      fetchAll();
    } catch (err) {
      showMsg(apiError(err, 'Failed'), 'danger');
    }
  };

  const handleUploadBranding = async (kind) => {
    const file = kind === 'logo' ? logoFile : faviconFile;
    if (!file || !branding.id) return;
    setUploadingBranding(p => ({ ...p, [kind]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      await client.post(`/orgs/${branding.id}/${kind}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMsg(`${kind === 'logo' ? 'Logo' : 'Favicon'} uploaded — reload to see changes`);
    } catch (err) {
      showMsg(apiError(err, 'Upload failed'), 'danger');
    } finally {
      setUploadingBranding(p => ({ ...p, [kind]: false }));
    }
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    if (!branding.id) return;
    try {
      await client.patch(`/orgs/${branding.id}`, brandingForm);
      showMsg('Branding saved — reload to see changes');
    } catch (err) {
      showMsg(apiError(err, 'Save failed'), 'danger');
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-danger" /></div>;
  }

  return (
    <div>
      {msg.text && (
        <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '14px' }}>
          {msg.text}
        </div>
      )}

      {/* Tab navigation */}
      <ul className="nav app-tabs mb-4">
        {ALL_TABS.filter(tab =>
          (!tab.featureKey || features[tab.featureKey] !== false) && (!tab.adminOnly || isAdmin) && (!tab.appAdminOnly || isAppAdmin)
        ).map(tab => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Data Imports */}
      {activeTab === 'imports' && (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
            Data Imports
          </div>
          <div className="card-body">
            <div className="row">
              {/* Sage */}
              <div className="col-md-4 d-flex flex-column" style={{ minHeight: '160px', paddingBottom: '1rem' }}>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>
                  Sage 50 Aged Creditors
                </label>
                <div className="text-muted mb-2" style={{ fontSize: '12px', minHeight: '18px' }}>
                  {config?.sage_data_date
                    ? `Last import: ${new Date(config.sage_data_date).toLocaleDateString('en-GB')}${config.sage_import_path ? ' (' + config.sage_import_path + ')' : ''}`
                    : ''}
                </div>
                <input
                  type="file"
                  className="form-control-file mb-2"
                  accept=".csv,.xlsx,.xls"
                  onChange={e => setSageFile(e.target.files[0])}
                />
                <button
                  className="btn btn-sm w-100 mt-auto"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                  onClick={handleSageImport}
                  disabled={importing.sage || !sageFile}
                >
                  {importing.sage
                    ? <><span className="spinner-border spinner-border-sm me-1" />Importing...</>
                    : 'Import Sage File'}
                </button>
              </div>

              {/* Syrinx */}
              <div className="col-md-4 d-flex flex-column" style={{ minHeight: '160px', paddingBottom: '1rem' }}>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>
                  Syrinx PO / Supplier Data
                </label>
                <div className="text-muted mb-2" style={{ fontSize: '12px', minHeight: '18px' }}>
                  {config?.syrinx_data_date
                    ? `Last import: ${new Date(config.syrinx_data_date).toLocaleDateString('en-GB')}${config.syrinx_import_path ? ' (' + config.syrinx_import_path + ')' : ''}`
                    : ''}
                </div>
                <input
                  type="file"
                  className="form-control-file mb-2"
                  accept=".csv,.xlsx,.xls"
                  onChange={e => setSyrinxFile(e.target.files[0])}
                />
                <button
                  className="btn btn-sm w-100 mt-auto"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                  onClick={handleSyrinxImport}
                  disabled={importing.syrinx || !syrinxFile}
                >
                  {importing.syrinx
                    ? <><span className="spinner-border spinner-border-sm me-1" />Importing...</>
                    : 'Import Syrinx File'}
                </button>
              </div>
              {/* Run Matching */}
              <div className="col-md-4 d-flex flex-column" style={{ minHeight: '160px', paddingBottom: '1rem' }}>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>
                  Run Matching & Reconciliation
                </label>
                <div className="text-muted mb-2" style={{ fontSize: '12px', minHeight: '18px' }} />
                <p className="mb-2" style={{ fontSize: '12px', color: '#6c757d' }}>
                  Builds the supplier master, matches invoices, and runs three-way reconciliation.
                </p>
                <button
                  className="btn btn-sm w-100 btn-outline-secondary mt-auto"
                  onClick={handleRunMatching}
                  disabled={importing.matching}
                >
                  {importing.matching
                    ? <><span className="spinner-border spinner-border-sm me-1" />Running...</>
                    : 'Run Matching'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Azure Configuration */}
      {activeTab === 'azure' && (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
            Azure Configuration
          </div>
          <div className="card-body">
            <form onSubmit={handleSaveAzure}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '13px' }}>Tenant ID</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={azureForm.tenant_id}
                    onChange={e => setAzureForm(p => ({ ...p, tenant_id: e.target.value }))}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '13px' }}>Client ID</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={azureForm.client_id}
                    onChange={e => setAzureForm(p => ({ ...p, client_id: e.target.value }))}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '13px' }}>Client Secret</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    value={azureForm.client_secret}
                    onChange={e => setAzureForm(p => ({ ...p, client_secret: e.target.value }))}
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '13px' }}>Document Intelligence Endpoint</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={azureForm.doc_intelligence_endpoint}
                    onChange={e => setAzureForm(p => ({ ...p, doc_intelligence_endpoint: e.target.value }))}
                    placeholder="https://your-resource.cognitiveservices.azure.com/"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontSize: '13px' }}>Document Intelligence Key</label>
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    value={azureForm.doc_intelligence_key}
                    onChange={e => setAzureForm(p => ({ ...p, doc_intelligence_key: e.target.value }))}
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label" style={{ fontSize: '13px' }}>Mailboxes (one per line)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={mailboxes}
                    onChange={e => setMailboxes(e.target.value)}
                    placeholder="purchaseinvoices@company.com&#10;accounts@company.com"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={handleSaveMailboxes}
                  >
                    Save Mailboxes
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-sm"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                >
                  Save Azure Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Matching Tolerances */}
      {activeTab === 'tolerances' && (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
            Matching Tolerances
          </div>
          <div className="card-body">
            <form onSubmit={handleSaveTolerance}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '13px' }}>Amount Tolerance (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm"
                    value={tolForm.amount_tolerance}
                    onChange={e => setTolForm(p => ({ ...p, amount_tolerance: e.target.value }))}
                  />
                  <div className="form-text" style={{ fontSize: '11px' }}>±£ allowed on invoice vs PO amounts</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '13px' }}>Tax Rounding (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control form-control-sm"
                    value={tolForm.tax_rounding_tolerance}
                    onChange={e => setTolForm(p => ({ ...p, tax_rounding_tolerance: e.target.value }))}
                  />
                  <div className="form-text" style={{ fontSize: '11px' }}>±£ allowed on VAT rounding</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '13px' }}>Date Tolerance (days)</label>
                  <input
                    type="number"
                    step="1"
                    className="form-control form-control-sm"
                    value={tolForm.date_tolerance_days}
                    onChange={e => setTolForm(p => ({ ...p, date_tolerance_days: e.target.value }))}
                  />
                  <div className="form-text" style={{ fontSize: '11px' }}>±days for date matching</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '13px' }}>Fuzzy Match Threshold (0-100)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    className="form-control form-control-sm"
                    value={tolForm.fuzzy_match_threshold}
                    onChange={e => setTolForm(p => ({ ...p, fuzzy_match_threshold: e.target.value }))}
                  />
                  <div className="form-text" style={{ fontSize: '11px' }}>Minimum fuzzy score for supplier matching</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: '13px' }}>Confidence Threshold (0-1)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    className="form-control form-control-sm"
                    value={tolForm.confidence_threshold}
                    onChange={e => setTolForm(p => ({ ...p, confidence_threshold: e.target.value }))}
                  />
                  <div className="form-text" style={{ fontSize: '11px' }}>Min extraction confidence before flagging</div>
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-sm"
                  style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                >
                  Save Tolerances
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Management */}
      {activeTab === 'users' && (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
            User Management
          </div>
          <div className="card-body">
            <div className="table-responsive mb-4">
              <table className="table table-sm" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-muted py-3">No users</td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>{u.display_name || '–'}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: '110px' }}
                          value={u.role}
                          onChange={e => handleUserRoleChange(u, e.target.value)}
                        >
                          <option value="reviewer">Reviewer</option>
                          <option value="approver">Approver</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`badge ${u.active ? 'bg-success' : 'bg-secondary'}`}>
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {u.last_seen_at ? new Date(u.last_seen_at).toLocaleDateString('en-GB') : '–'}
                      </td>
                      <td>
                        {u.active ? (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{ fontSize: '11px', padding: '2px 8px' }}
                            onClick={() => handleDeactivate(u)}
                          >
                            Deactivate
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-top pt-3">
              <h6 style={{ fontSize: '14px', marginBottom: '12px' }}>Add New User</h6>
              <form onSubmit={handleCreateUser} autoComplete="off">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label mb-1" style={{ fontSize: '12px' }}>Email</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={newUser.email}
                      onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                      autoComplete="off"
                      placeholder="new.user@company.com"
                      required
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label mb-1" style={{ fontSize: '12px' }}>Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={newUser.display_name}
                      onChange={e => setNewUser(p => ({ ...p, display_name: e.target.value }))}
                      autoComplete="off"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label mb-1" style={{ fontSize: '12px' }}>Role</label>
                    <select
                      className="form-select form-select-sm"
                      value={newUser.role}
                      onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}
                    >
                      <option value="reviewer">Reviewer</option>
                      <option value="approver">Approver</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1" style={{ fontSize: '12px' }}>Temporary Password</label>
                    <input
                      type="password"
                      className="form-control form-control-sm"
                      value={newUser.password}
                      onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                      autoComplete="new-password"
                      placeholder="Min. 8 characters"
                      required
                    />
                  </div>
                  <div className="col-auto">
                    <button
                      type="submit"
                      className="btn btn-sm"
                      style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                    >
                      Add User
                    </button>
                  </div>
                </div>
              </form>
            </div>

          </div>
        </div>
      )}

      {/* Branding */}
      {activeTab === 'branding' && (
        <div className="card">
          <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
            Branding
          </div>
          <div className="card-body" style={{ maxWidth: '560px' }}>
            {/* Display name + colour */}
            <form onSubmit={handleSaveBranding} className="mb-4">
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: '13px' }}>Display Name (shown in sidebar &amp; navbar)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={brandingForm.display_name}
                  onChange={e => setBrandingForm(p => ({ ...p, display_name: e.target.value }))}
                  placeholder="THX AP Automation"
                />
              </div>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: '13px' }}>Primary Colour</label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    className="form-control form-control-sm"
                    style={{ width: '52px', padding: '2px' }}
                    value={brandingForm.primary_color}
                    onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={brandingForm.primary_color}
                    onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))}
                    style={{ fontFamily: 'monospace', maxWidth: '120px' }}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-sm"
                style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}>
                Save Branding
              </button>
            </form>

            <hr />

            {/* Logo upload */}
            <div className="mb-4">
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                Organisation Logo <span className="text-muted" style={{ fontWeight: 400 }}>(PNG or JPG — replaces the text name in the sidebar)</span>
              </label>
              {branding.logo_url && (
                <div className="mb-2">
                  <img src={branding.logo_url} alt="Current logo"
                    style={{ maxHeight: '40px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }} />
                  <div className="text-muted mt-1" style={{ fontSize: '11px' }}>Current logo</div>
                </div>
              )}
              <input type="file" className="form-control-file mb-2" accept=".png,.jpg,.jpeg"
                onChange={e => setLogoFile(e.target.files[0])} />
              <button className="btn btn-sm btn-outline-secondary"
                onClick={() => handleUploadBranding('logo')}
                disabled={!logoFile || uploadingBranding.logo}>
                {uploadingBranding.logo ? 'Uploading…' : 'Upload Logo'}
              </button>
            </div>

            {/* Favicon upload */}
            <div>
              <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                Favicon <span className="text-muted" style={{ fontWeight: 400 }}>(ICO or PNG — shown in browser tab)</span>
              </label>
              {branding.favicon_url && (
                <div className="mb-2">
                  <img src={branding.favicon_url} alt="Current favicon"
                    style={{ width: '32px', height: '32px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '2px' }} />
                  <div className="text-muted mt-1" style={{ fontSize: '11px' }}>Current favicon</div>
                </div>
              )}
              <input type="file" className="form-control-file mb-2" accept=".ico,.png"
                onChange={e => setFaviconFile(e.target.files[0])} />
              <button className="btn btn-sm btn-outline-secondary"
                onClick={() => handleUploadBranding('favicon')}
                disabled={!faviconFile || uploadingBranding.favicon}>
                {uploadingBranding.favicon ? 'Uploading…' : 'Upload Favicon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Settings */}
      {activeTab === 'export' && (
        <div>
          {/* Export card */}
          <div className="card mb-4">
            <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
              Export Organisation Data
            </div>
            <div className="card-body">
              <p style={{ fontSize: '13px', color: '#495057', marginBottom: '20px' }}>
                Download a complete copy of your organisation's data. This includes all users,
                emails, invoices, credit notes, exceptions, and processing history.
                Use this for backups or GDPR subject access requests.
              </p>
              <div className="d-flex" style={{ gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '16px', minWidth: '220px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                    <i className="bi bi-file-earmark-code mr-2" style={{ color: '#0d6efd' }} />
                    Full Export (JSON)
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px' }}>
                    Complete dataset — users, invoices, emails, exceptions, processing runs.
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleExport('json')}
                    disabled={exporting === 'json'}
                  >
                    {exporting === 'json' ? 'Preparing…' : 'Download JSON'}
                  </button>
                </div>

                <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '16px', minWidth: '220px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                    <i className="bi bi-file-earmark-spreadsheet mr-2" style={{ color: '#198754' }} />
                    Invoices (CSV)
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px' }}>
                    Invoice records only — compatible with Excel and Google Sheets.
                  </div>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => handleExport('csv')}
                    disabled={exporting === 'csv'}
                  >
                    {exporting === 'csv' ? 'Preparing…' : 'Download CSV'}
                  </button>
                </div>
              </div>

              <div className="mt-3" style={{ fontSize: '11px', color: '#adb5bd' }}>
                <i className="bi bi-shield-lock mr-1" />
                Exported files are generated on demand and not stored on our servers.
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div style={{ border: '1px solid #f5c6cb', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#fff5f5', padding: '10px 16px', borderBottom: '1px solid #f5c6cb' }}>
              <strong style={{ fontSize: '14px', color: '#842029' }}>Danger Zone</strong>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#212529' }}>
                Delete this organisation
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', marginBottom: '16px' }}>
                Once you delete this organisation, there is no going back. All users, invoices,
                emails, and data will be permanently erased. This action cannot be undone.
              </div>

              <div style={{ padding: '16px', backgroundColor: '#fff8f8', border: '1px solid #f5c6cb', borderRadius: '4px', maxWidth: '480px' }}>

                {/* Step 1 — type org name */}
                <div style={{ fontSize: '12px', color: '#842029', marginBottom: '6px' }}>
                  <strong>Step 1</strong> — Type the organisation name to continue:
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder=""
                  value={deleteConfirm}
                  onChange={e => { setDeleteConfirm(e.target.value); setDeleteStep2(false); setDeleteWord(''); }}
                  style={{ marginBottom: '10px', fontFamily: 'monospace' }}
                  autoComplete="off"
                  disabled={deleteStep2}
                />
                {!deleteStep2 && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setDeleteStep2(true)}
                    disabled={deleteConfirm.trim() !== (branding.display_name || '').trim()}
                  >
                    I understand, delete this organisation
                  </button>
                )}

                {/* Step 2 — type "delete" */}
                {deleteStep2 && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f5c6cb' }}>
                    <div style={{ fontSize: '12px', color: '#842029', marginBottom: '6px' }}>
                      <strong>Step 2</strong> — Type <strong style={{ fontFamily: 'monospace' }}>delete</strong> to permanently erase all data:
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder=""
                      value={deleteWord}
                      onChange={e => setDeleteWord(e.target.value)}
                      style={{ marginBottom: '10px', fontFamily: 'monospace' }}
                      autoComplete="off"
                      autoFocus
                    />
                    <div className="d-flex" style={{ gap: '8px' }}>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleDeleteOrg}
                        disabled={deleting || deleteWord.trim() !== 'delete'}
                      >
                        <i className="bi bi-trash mr-1" />
                        {deleting ? 'Deleting…' : 'Permanently delete organisation'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setDeleteStep2(false); setDeleteConfirm(''); setDeleteWord(''); }}
                        disabled={deleting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
